import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { useQuery } from "@tanstack/react-query";
import type { ExtractRouteParams, RouteComponentProps } from "react-router";
import { generatePath } from "react-router-dom";
import { useFeatureFlags } from "swing-components";

import { ContentSingleColumn, useAuth } from "~components";
import { InvalidConfiguration, OnboardingPage, WebOnlyConfiguration } from "~onboarding/components";
import { computeGroupsState, getConfiguration, type TaskComputed } from "~onboarding/utils";
import { GET, SubAttribute } from "~utils";

import { ONBOARDING_ROUTES } from "../index";
import { TasksPageBase } from "./TasksPageBase";

/***** Component *****/
type TasksPageProps = RouteComponentProps<
  ExtractRouteParams<(typeof ONBOARDING_ROUTES)["tasks"], string>
>;

/**
 * A page which shows the onboarding sub all the tasks they must complete
 * before to finish the onboarding process
 *
 * Assumptions:
 * - The sub is authenticated via `useAuth`
 * - The sub is not rejected or waitlisted
 */
export function TasksPage(props: TasksPageProps) {
  const { history, match } = props;
  const { onboardingConfigurationId } = match.params;

  /***** Hooks *****/
  const { refreshUser, userInfo } = useAuth();
  const { flags } = useFeatureFlags();

  /***** Effects *****/
  /**
   * When the page mounts, refresh the sub's authentication details in case
   * they were rejected via `onboardingStatus === "REJECTED"`.
   */
  useEffect(() => {
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /***** Queries ******/
  const { data: onboardingConfiguration, isError: isConfigurationError } = useQuery({
    // TODO: This query key is too complex. We should simplify it.
    queryKey: [
      "configuration",
      onboardingConfigurationId,
      onboardingConfigurationId === "ca-v4" ? flags?.onboardingTask : undefined,
      onboardingConfigurationId === "nj" ? flags?.onboardingProfileRequired : undefined,
      flags?.permitAssistLa,
      userInfo?.permitTask,
    ],
    queryFn: () => getConfiguration(onboardingConfigurationId, { featureFlags: flags, userInfo }),
    // Cache the configuration for the lifetime of the application since this
    // only changes on new deployments which will require an application refresh.
    // This will make all future calls using this key to not make a network request.
    staleTime: Infinity,
    // Disabling retries since the query is a lazy file import load, thus if
    // there is an error, it's on purpose.
    retry: false,
  });

  const { data: subAttributes, isError: isSubAttributesError } = useQuery({
    queryKey: ["attributes"],
    queryFn: () => GET("/api/sub/attributes"),
    // Since the response is `{ data: [...subAttribute] }` we can clean up the response
    select: (data) =>
      data.data.map((attribute) => ({
        ...attribute,
        updatedAt: new Date(attribute.updatedAt),
      })) as Array<SubAttribute>,
    // Set to 500ms (compared to 1000ms graduated backoff) to avoid long waits
    // to fail.
    retryDelay: 500,
  });

  /*** Render #1 - Mobile Device ***/
  if (Capacitor.isNativePlatform()) {
    return (
      <OnboardingPage>
        <ContentSingleColumn>
          <WebOnlyConfiguration />
        </ContentSingleColumn>
      </OnboardingPage>
    );
  }

  /*** Render #2 - Invalidate Configuration or Sub Attributes ***/
  if (isSubAttributesError === true || isConfigurationError === true) {
    return (
      <OnboardingPage>
        <ContentSingleColumn>
          <InvalidConfiguration />
        </ContentSingleColumn>
      </OnboardingPage>
    );
  }

  /*** Render #3 - Loading ***/
  if (
    // Loading Sub Attributes
    subAttributes === undefined ||
    // Loading Configuration
    onboardingConfiguration === undefined
  ) {
    return <OnboardingPage isLoading />;
  }

  /*** Constants ***/
  const groupsComputed = computeGroupsState({
    groups: onboardingConfiguration.groups,
    syncedSubAttributes: subAttributes,
  });

  /***** Handlers *****/
  function handleTaskClick(task: TaskComputed) {
    const flatTasks = groupsComputed.flatMap((group) => group.tasks);
    const taskIdOrIndex = task.id ?? flatTasks.findIndex((t) => t.name === task.name);
    if (taskIdOrIndex === -1) {
      // eslint-disable-next-line no-console
      console.error(`Task "${task.name}" not found`);
      return;
    }
    history.push(
      generatePath(ONBOARDING_ROUTES["task"], {
        onboardingConfigurationId,
        task: taskIdOrIndex,
      }),
    );
  }

  /*** Render #4 ***/
  return (
    <OnboardingPage isLoading={!groupsComputed}>
      <ContentSingleColumn>
        {groupsComputed && (
          <TasksPageBase groupsComputed={groupsComputed} onTaskClick={handleTaskClick} />
        )}
      </ContentSingleColumn>
    </OnboardingPage>
  );
}
